import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { Buffer } from 'buffer';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, Spinner } from '@printdeal/ui-library';
import { SanitizedOrder } from '../../../../../../types/order/Order';
import useDownloadInvoice from './useDownloadInvoice';
import { ServiceFactory } from '../../../../../../services/ServiceFactory';
import { dispatchAccountEvent } from '../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';

enum Status {
  IDLE = 'idle',
  DOWNLOADING = 'downloading',
  LOADED = 'loaded',
}
interface CancelOrderButtonProps {
  order: SanitizedOrder,
}

const DownloadInvoiceButton = ({ order }: CancelOrderButtonProps) => {
  const queryClient = useQueryClient();
  const isInvoiceDownloadable = useDownloadInvoice(order);
  const [downloadInvoiceStatus, setDownloadInvoiceStatus] = useState<Status>(Status.IDLE);
  const intl = useIntl();

  if (!isInvoiceDownloadable.enabled) {
    return null;
  }

  const downloadInvoice = async (name: string) => {
    window.Buffer = Buffer;
    const invoice = await queryClient.fetchQuery({
      queryKey: ['order', 'fetch-invoice', order.id, name],
      queryFn: () => ServiceFactory.createInvoiceService().downloadInvoice(name),
    });
    const blob = new Blob([Buffer.from(invoice, 'base64')], { type: 'application/pdf' });
    saveAs(blob, name);
  };

  const handleDownloadInvoices = async () => {
    setDownloadInvoiceStatus(Status.DOWNLOADING);
    await Promise.all(isInvoiceDownloadable.invoices.map((invoice) => downloadInvoice(invoice.invoiceNumber)));
    setDownloadInvoiceStatus(Status.LOADED);
    dispatchAccountEvent('order_detail_interaction', 'download_invoice');
  };

  return (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <Link
      variant="secondary"
      as="button"
      onClick={handleDownloadInvoices}
      className="pd-text-xs"
      iconBefore={downloadInvoiceStatus !== Status.DOWNLOADING ? 'download' : undefined}
    >
      {downloadInvoiceStatus === Status.DOWNLOADING && (
        <Spinner
          variant="dim"
          size="md"
          screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
        />
      )}
      <FormattedMessage
        id="account.details.button.download.invoice"
        defaultMessage="Download invoice"
      />
    </Link>
  );
};

export default DownloadInvoiceButton;
